/**
 * Preisrechner für Angebote (super_admin) — die ganze Rechnung, ohne Angular.
 *
 * Ein Szenario wird immer zweifach gerechnet:
 *   Managed  = Plattformlizenz + Freimenge × Credit-Preis, Mehrverbrauch dazu
 *   BYOK     = Plattformlizenz × Faktor, Vorgänge über der Inklusivmenge dazu
 *   SLA      = Pauschale + Stunden über der Inklusivmenge (in beiden Varianten gleich)
 * Monatswerte; Jahr = × 12, Jahr 1 = Jahr + Einrichtung + Pilot.
 * Kosten sind das, was je Angebot wirklich anfällt: KI-Kosten des Verbrauchs
 * und SLA-Stunden. Die Infrastruktur (300–400 € insgesamt) verteilt sich auf
 * alles und gehört in kein einzelnes Angebot (Lasse, 30.10.2026). Die KI-Kosten
 * je Credit kommen aus dem Deckungsbeitrag auf Token-Analytics (`measureAiCost`).
 * Die Nutzung hat drei Fälle (niedrig / erwartet / hoch); gerechnet wird der
 * gewählte, `computeUsageCases` rechnet alle drei für die Gegenüberstellung.
 *
 * Reproduziert die Rechnung vom 05.10.2026 (8.000 Lernende, 0,30 €, 1 Credit
 * → 2.720 €/Monat; BYOK × 1,5 → 3.600 €). Doku:
 * features/docs/technical/pricing-calculator.md
 */

export type PlatformMode = 'fixed' | 'tiered';
export type UsageCase = 'low' | 'mid' | 'high';
export const USAGE_CASES: UsageCase[] = ['low', 'mid', 'high'];

export type SlaLevelId = 'none' | 'basic' | 'standard' | 'premium' | 'enterprise';

/**
 * SLA-Stufen (Vorschlag 28.10.2026, nicht entschieden). Die Stufe füllt
 * Pauschale, Inklusivstunden und Stundensatz vor — im Angebot bleiben die
 * Werte frei änderbar. Reaktionszeiten in Geschäftsstunden; Texte (Kanäle,
 * Servicezeiten) in i18n `pages.admin.pricing.sla.levels.<id>`.
 */
export interface SlaLevel {
  id: SlaLevelId;
  flatMonthly: number;
  includedHours: number;
  hourlyRate: number;
  /** Reaktionszeit kritisch / normal in Stunden (Geschäftszeit); 0 = keine Zusage. */
  responseCriticalH: number;
  responseNormalH: number;
  phone: boolean;
}

export const SLA_LEVELS: SlaLevel[] = [
  { id: 'none', flatMonthly: 0, includedHours: 0, hourlyRate: 0, responseCriticalH: 0, responseNormalH: 0, phone: false },
  { id: 'basic', flatMonthly: 0, includedHours: 0, hourlyRate: 120, responseCriticalH: 16, responseNormalH: 24, phone: false },
  { id: 'standard', flatMonthly: 290, includedHours: 2, hourlyRate: 110, responseCriticalH: 8, responseNormalH: 16, phone: false },
  { id: 'premium', flatMonthly: 790, includedHours: 6, hourlyRate: 100, responseCriticalH: 4, responseNormalH: 8, phone: true },
  { id: 'enterprise', flatMonthly: 1790, includedHours: 15, hourlyRate: 95, responseCriticalH: 1, responseNormalH: 4, phone: true },
];

/** Eingabefeld je Nutzungsfall. */
export const USAGE_FIELD: Record<UsageCase, 'usageLowPerLearner' | 'usagePerLearner' | 'usageHighPerLearner'> = {
  low: 'usageLowPerLearner',
  mid: 'usagePerLearner',
  high: 'usageHighPerLearner',
};

export function slaLevel(id: SlaLevelId): SlaLevel {
  return SLA_LEVELS.find((l) => l.id === id) ?? SLA_LEVELS[0];
}

export interface Scenario {
  id: string;
  name: string;
  learners: number;
  /** Nur Info — Lehrende kosten im Modell nichts. */
  teachers: number;
  platformMode: PlatformMode;
  /** € je Lernendem/Monat im Modus `fixed`. */
  platformPerLearner: number;
  /** Inklusive Credits je Lernendem/Monat. */
  includedPerLearner: number;
  /** € netto je Credit (Freimenge und Mehrverbrauch). */
  creditPrice: number;
  /** Mengenstaffel auf den Monatsverbrauch statt eines festen Credit-Preises. */
  creditTiers: boolean;
  /** Erwarteter Verbrauch je Lernendem/Monat in Credits (Fall „erwartet"). */
  usagePerLearner: number;
  /** Verbrauch im Fall „niedrig" / „hoch". */
  usageLowPerLearner: number;
  usageHighPerLearner: number;
  /** Welcher Nutzungsfall die Hauptzahlen bestimmt. */
  usageCase: UsageCase;
  /** % Rabatt auf die Grundgebühr. */
  discountPct: number;
  byokFactor: number;
  byokIncludedOps: number;
  /** € je 1.000 Vorgänge über der Inklusivmenge. */
  byokOverPer1000: number;
  setupFee: number;
  pilotFee: number;
  slaLevel: SlaLevelId;
  /** SLA-Pauschale €/Monat (enthält die Inklusivstunden). */
  slaFlatMonthly: number;
  slaIncludedHours: number;
  /** € je Stunde über der Inklusivmenge. */
  slaHourlyRate: number;
  /** Erwartete Support-Stunden je Monat (variabel). */
  slaExpectedHours: number;
}

export type AiCostSource = 'measured' | 'manual';

export interface CostAssumptions {
  /** Woher die KI-Kosten je Credit kommen: Messung oder `llmPerCredit`. */
  llmSource: AiCostSource;
  /** Eigener Wert für die KI-Kosten je Credit (Managed), gilt bei `manual` und ohne Messung. */
  llmPerCredit: number;
  /** Optional: Betrieb & Support € je Lernendem/Monat. Standard 0 — nichts Pauschales in der Marge. */
  supportPerLearner: number;
  /** Vorgänge je verbrauchtem Credit (BYOK zählt Vorgänge, nicht Credits). */
  opsPerCredit: number;
  /** Was bei BYOK je Vorgang bei uns bleibt (Einbettungen, Tracing, Hintergrund). */
  byokResidualPerOp: number;
  /** Liste: Plattformanteil je Lernendem (Basic/Pro minus Credit-Wert). */
  listPlatformPerLearner: number;
  /** Liste: € je Credit. */
  listCreditPrice: number;
  /** Unsere Kosten je SLA-Stunde (intern, voll belastet). */
  slaCostPerHour: number;
}

export interface VariantResult {
  platformLicense: number;
  baseFee: number;
  includedCredits: number;
  /** Mehrverbrauch (Managed: Credits, BYOK: Vorgänge) in Einheiten. */
  overUnits: number;
  overCharge: number;
  /** SLA-Umsatz €/Monat (Pauschale + Mehrstunden), in `monthly` enthalten. */
  slaCharge: number;
  monthly: number;
  /** KI-Kosten €/Monat, in `costMonthly` enthalten. */
  aiCost: number;
  annual: number;
  year1: number;
  perLearnerMonth: number;
  perLearnerYear: number;
  costMonthly: number;
  contributionMonthly: number;
  contributionAnnual: number;
  /** Deckungsbeitrag in % des Umsatzes; 0, wenn kein Umsatz. */
  marginPct: number;
}

export interface OfferResult {
  platformPerLearnerEff: number;
  usedCredits: number;
  managed: VariantResult;
  byok: VariantResult;
  /** Liste für dasselbe Kontingent (max. aus Freimenge und Verbrauch). */
  listMonthly: number;
  /** 1 − Managed/Liste, in %. */
  savingsVsListPct: number;
  /** Plattformpreis je Lernendem, bei dem Managed die Ziel-Marge erreicht. */
  platformForMargin: { breakEven: number; m40: number; m50: number };
}

export interface Tier {
  /** Obergrenze (inklusive); `Infinity` für die letzte Stufe. */
  upTo: number;
  price: number;
}

/** Plattform-Grenzpreise je Lernendem (Vorschlag 05.10.2026). */
export const PLATFORM_TIERS: Tier[] = [
  { upTo: 250, price: 0.6 },
  { upTo: 1000, price: 0.48 },
  { upTo: 2500, price: 0.4 },
  { upTo: 5000, price: 0.34 },
  { upTo: 10000, price: 0.28 },
  { upTo: 25000, price: 0.22 },
  { upTo: Infinity, price: 0.18 },
];

/** Credit-Mengenstaffel auf den Monatsverbrauch. */
export const CREDIT_TIERS: Tier[] = [
  { upTo: 50000, price: 0.04 },
  { upTo: 150000, price: 0.036 },
  { upTo: Infinity, price: 0.032 },
];

export const DEFAULT_COSTS: CostAssumptions = {
  llmSource: 'measured',
  llmPerCredit: 0.019,
  supportPerLearner: 0,
  opsPerCredit: 1,
  byokResidualPerOp: 0.001,
  listPlatformPerLearner: 0.6,
  listCreditPrice: 0.04,
  slaCostPerHour: 65,
};

/** Gesamtzahlen aus `GET /admin/token-stats/margin` (`margin_report`). */
export interface MarginTotals {
  days: number;
  window_start: string;
  eur_per_credit: number;
  total: {
    revenue_eur: number;
    cost_eur: number;
    margin_pct: number | null;
    overhead_share_pct: number | null;
    unpriced_runs: number;
  };
}

export interface MeasuredAiCost {
  /** KI-Kosten je verbrauchtem Credit, Hintergrundarbeit eingerechnet. */
  costPerCredit: number;
  credits: number;
  costEur: number;
  marginPct: number | null;
  overheadSharePct: number | null;
  unpricedRuns: number;
  days: number;
  windowStart: string;
}

/** Unter dieser Menge sagt der Schnitt nichts — dann gilt der eigene Wert. */
export const MIN_MEASURED_CREDITS = 100;

/**
 * KI-Kosten je Credit aus dem Deckungsbeitrag. `revenue_eur` ist der Verbrauch
 * zum Credit-Anker, also Credits = Erlös / € je Credit; `cost_eur` enthält
 * alle LLM-Läufe samt Hintergrund (Analytics, Lernerprofil, nicht gebuchte
 * Turns) — die fallen mit der Nutzung an und gehören in die Marge.
 */
export function measureAiCost(r: MarginTotals | null | undefined): MeasuredAiCost | null {
  if (!r || !(r.eur_per_credit > 0) || !(r.total?.cost_eur > 0)) return null;
  const credits = r.total.revenue_eur / r.eur_per_credit;
  if (!(credits >= MIN_MEASURED_CREDITS)) return null;
  return {
    costPerCredit: r.total.cost_eur / credits,
    credits,
    costEur: r.total.cost_eur,
    marginPct: r.total.margin_pct,
    overheadSharePct: r.total.overhead_share_pct,
    unpricedRuns: r.total.unpriced_runs,
    days: r.days,
    windowStart: r.window_start,
  };
}

/** Annahmen, mit denen gerechnet wird: die Messung, solange sie gilt. */
export function effectiveCosts(c: CostAssumptions, measured: MeasuredAiCost | null): CostAssumptions {
  return c.llmSource === 'measured' && measured ? { ...c, llmPerCredit: measured.costPerCredit } : c;
}

/** Gespeicherte Annahmen lesen: bis 30.10.2026 hingen Fixbeträge daran. */
export function readStoredCosts(stored: Partial<CostAssumptions> & { infraFixed?: number }): CostAssumptions {
  const { infraFixed: _infra, ...rest } = stored;
  const costs = { ...DEFAULT_COSTS, ...rest };
  // 0,125 € war kein Messwert, sondern die alte Pauschale auf Lernende umgelegt.
  if (costs.supportPerLearner === 0.125) costs.supportPerLearner = 0;
  return costs;
}

let idSeq = 0;
export function newId(): string {
  idSeq += 1;
  return `s${Date.now().toString(36)}${idSeq}`;
}

export function defaultScenario(overrides: Partial<Scenario> = {}): Scenario {
  return {
    id: newId(),
    name: '8.000 Lernende',
    learners: 8000,
    teachers: 1000,
    platformMode: 'fixed',
    platformPerLearner: 0.3,
    includedPerLearner: 1,
    creditPrice: 0.04,
    creditTiers: false,
    usagePerLearner: 3,
    usageLowPerLearner: 1,
    usageHighPerLearner: 6,
    usageCase: 'mid',
    discountPct: 0,
    byokFactor: 1.5,
    byokIncludedOps: 100000,
    byokOverPer1000: 5,
    setupFee: 9500,
    pilotFee: 0,
    slaLevel: 'none',
    slaFlatMonthly: 0,
    slaIncludedHours: 0,
    slaHourlyRate: 0,
    slaExpectedHours: 0,
    ...overrides,
  };
}

/**
 * Szenarien aus dem Browser, die noch nicht gespeichert sind. `known` = schon
 * gespeicherte Angebote und unveränderte Vorlagen; gleich ist, was in Name und
 * Grundpreisen übereinstimmt.
 */
export function legacyToImport(legacy: Scenario[], known: Scenario[]): Scenario[] {
  const print = (s: Scenario) =>
    [s.name.trim(), s.learners, s.platformPerLearner, s.includedPerLearner, s.creditPrice].join('|');
  const seen = new Set(known.map(print));
  return legacy.filter((s) => {
    const key = print(s);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Summe über Grenzpreise: jede Einheit kostet den Preis ihrer Stufe. */
export function graduated(units: number, tiers: Tier[]): number {
  let rest = Math.max(0, units);
  let lower = 0;
  let sum = 0;
  for (const t of tiers) {
    if (rest <= 0) break;
    const span = Math.min(rest, t.upTo - lower);
    sum += span * t.price;
    rest -= span;
    lower = t.upTo;
  }
  return sum;
}

/** Plattformlizenz €/Monat. */
export function platformFee(s: Scenario): number {
  const learners = Math.max(0, s.learners);
  return s.platformMode === 'tiered'
    ? graduated(learners, PLATFORM_TIERS)
    : learners * s.platformPerLearner;
}

/** € für `credits` Credits im Monat (Staffel auf die gesamte Monatsmenge). */
export function creditCharge(credits: number, s: Scenario): number {
  if (credits <= 0) return 0;
  return s.creditTiers ? graduated(credits, CREDIT_TIERS) : credits * s.creditPrice;
}

/** Verbrauch je Lernendem im gewählten Nutzungsfall. */
export function usageOf(s: Scenario, usageCase: UsageCase = s.usageCase): number {
  if (usageCase === 'low') return s.usageLowPerLearner;
  if (usageCase === 'high') return s.usageHighPerLearner;
  return s.usagePerLearner;
}

/** SLA €/Monat: Pauschale + Stunden über der Inklusivmenge. */
export function slaRevenue(s: Scenario): number {
  if (s.slaLevel === 'none') return 0;
  const over = Math.max(0, s.slaExpectedHours - s.slaIncludedHours);
  return Math.max(0, s.slaFlatMonthly) + over * Math.max(0, s.slaHourlyRate);
}

/** Unsere SLA-Kosten €/Monat: erwartete Stunden × interner Stundensatz. */
export function slaCost(s: Scenario, c: CostAssumptions): number {
  if (s.slaLevel === 'none') return 0;
  return Math.max(0, s.slaExpectedHours) * c.slaCostPerHour;
}

/** SLA-Stufe übernehmen: füllt Pauschale, Inklusivstunden und Satz vor. */
export function withSlaLevel(s: Scenario, id: SlaLevelId): Scenario {
  const l = slaLevel(id);
  return {
    ...s,
    slaLevel: l.id,
    slaFlatMonthly: l.flatMonthly,
    slaIncludedHours: l.includedHours,
    slaHourlyRate: l.hourlyRate,
    slaExpectedHours: l.id === 'none' ? 0 : s.slaExpectedHours || l.includedHours,
  };
}

function variant(
  platformLicense: number,
  baseFee: number,
  includedCredits: number,
  overUnits: number,
  overCharge: number,
  slaCharge: number,
  aiCost: number,
  otherCost: number,
  s: Scenario,
): VariantResult {
  const monthly = baseFee + overCharge + slaCharge;
  const annual = monthly * 12;
  const learners = Math.max(1, s.learners);
  const costMonthly = aiCost + otherCost;
  const contributionMonthly = monthly - costMonthly;
  return {
    platformLicense,
    baseFee,
    includedCredits,
    overUnits,
    overCharge,
    slaCharge,
    monthly,
    aiCost,
    annual,
    year1: annual + s.setupFee + s.pilotFee,
    perLearnerMonth: monthly / learners,
    perLearnerYear: annual / learners,
    costMonthly,
    contributionMonthly,
    contributionAnnual: contributionMonthly * 12,
    marginPct: monthly > 0 ? (contributionMonthly / monthly) * 100 : 0,
  };
}

export function computeOffer(s: Scenario, c: CostAssumptions, usageCase: UsageCase = s.usageCase): OfferResult {
  const learners = Math.max(0, s.learners);
  const keep = 1 - s.discountPct / 100;
  const license = platformFee(s);
  const included = learners * Math.max(0, s.includedPerLearner);
  const used = learners * Math.max(0, usageOf(s, usageCase));
  const sla = slaRevenue(s);
  const otherCost = c.supportPerLearner * learners + slaCost(s, c);

  // Managed: Freimenge ist in der Grundgebühr, Mehrverbrauch zum Grenzpreis
  // derselben Staffel (Credits über der Freimenge setzen dort an, wo sie endet).
  const includedValue = creditCharge(included, s);
  const managedBase = (license + includedValue) * keep;
  const overCredits = Math.max(0, used - included);
  const managedOver = used > included ? creditCharge(used, s) - creditCharge(included, s) : 0;
  const managed = variant(
    license * keep,
    managedBase,
    included,
    overCredits,
    managedOver,
    sla,
    used * c.llmPerCredit,
    otherCost,
    s,
  );

  // BYOK: Modellkosten trägt der Kunde; wir verdienen an der Plattform.
  const ops = used * c.opsPerCredit;
  const overOps = Math.max(0, ops - s.byokIncludedOps);
  const byokLicense = license * s.byokFactor * keep;
  const byok = variant(
    byokLicense,
    byokLicense,
    0,
    overOps,
    (overOps / 1000) * s.byokOverPer1000,
    sla,
    ops * c.byokResidualPerOp,
    otherCost,
    s,
  );

  const listMonthly =
    learners * c.listPlatformPerLearner + Math.max(included, used) * c.listCreditPrice;

  // Plattformpreis, der Managed auf die Ziel-Marge bringt (linear in p):
  // Umsatz = (L·p + Freimengenwert)·keep + Mehrverbrauch + SLA  =  Kosten / (1 − m)
  const forMargin = (m: number) => {
    if (learners <= 0 || keep <= 0) return 0;
    const target = managed.costMonthly / (1 - m);
    const p = ((target - managedOver - sla) / keep - includedValue) / learners;
    return Math.max(0, p);
  };

  return {
    platformPerLearnerEff: learners > 0 ? license / learners : 0,
    usedCredits: used,
    managed,
    byok,
    listMonthly,
    // SLA hat keinen Listenpreis — der Vergleich läuft ohne ihn.
    savingsVsListPct: listMonthly > 0 ? (1 - (managed.monthly - sla) / listMonthly) * 100 : 0,
    platformForMargin: { breakEven: forMargin(0), m40: forMargin(0.4), m50: forMargin(0.5) },
  };
}

/** Alle drei Nutzungsfälle eines Szenarios — für die Gegenüberstellung. */
export function computeUsageCases(s: Scenario, c: CostAssumptions): Record<UsageCase, OfferResult> {
  return {
    low: computeOffer(s, c, 'low'),
    mid: computeOffer(s, c, 'mid'),
    high: computeOffer(s, c, 'high'),
  };
}

// ── Matrix ─────────────────────────────────────────────────────────────

export type AxisField =
  | 'learners'
  | 'platformPerLearner'
  | 'includedPerLearner'
  | 'usagePerLearner'
  | 'creditPrice'
  | 'byokFactor'
  | 'discountPct';

export interface AxisSpec {
  field: AxisField;
  start: number;
  step: number;
  count: number;
}

/** Sinnvolle Startwerte je Achse. */
export const AXIS_DEFAULTS: Record<AxisField, Omit<AxisSpec, 'field'>> = {
  learners: { start: 2000, step: 2000, count: 8 },
  platformPerLearner: { start: 0.2, step: 0.05, count: 9 },
  includedPerLearner: { start: 1, step: 1, count: 10 },
  usagePerLearner: { start: 1, step: 1, count: 10 },
  creditPrice: { start: 0.03, step: 0.005, count: 7 },
  byokFactor: { start: 1, step: 0.25, count: 7 },
  discountPct: { start: 0, step: 5, count: 7 },
};

export type MatrixMetric = 'monthly' | 'annual' | 'perLearnerMonth' | 'marginPct' | 'contributionAnnual';
export type MatrixVariant = 'managed' | 'byok' | 'diff';

export interface MatrixCell {
  row: number;
  col: number;
  value: number;
  /** Marge der Variante (bei `diff` die von Managed) — für die Tönung. */
  marginPct: number;
  isBase: boolean;
}

export interface MatrixResult {
  rowValues: number[];
  colValues: number[];
  cells: MatrixCell[][];
}

/** Werte einer Achse; Rundung gegen Gleitkomma-Reste (0,30000000004). */
export function axisValues(a: AxisSpec): number[] {
  const n = Math.max(1, Math.min(20, Math.round(a.count)));
  return Array.from({ length: n }, (_, i) => Math.round((a.start + i * a.step) * 1e6) / 1e6);
}

/** Szenario mit gesetztem Feld; Plattformpreis erzwingt den festen Modus. */
export function withField(s: Scenario, field: AxisField, value: number): Scenario {
  const next: Scenario = { ...s, [field]: value };
  if (field === 'platformPerLearner') next.platformMode = 'fixed';
  if (field === 'creditPrice') next.creditTiers = false;
  // Eine Nutzungsachse setzt den Fall „erwartet" — sonst rechnete die Matrix
  // mit dem gewählten Fall und die Achse liefe ins Leere.
  if (field === 'usagePerLearner') next.usageCase = 'mid';
  return next;
}

export function metricOf(r: OfferResult, metric: MatrixMetric, v: MatrixVariant): number {
  if (v === 'diff') return r.byok[metric] - r.managed[metric];
  return r[v][metric];
}

export function buildMatrix(
  base: Scenario,
  costs: CostAssumptions,
  rows: AxisSpec,
  cols: AxisSpec,
  metric: MatrixMetric,
  v: MatrixVariant,
): MatrixResult {
  const rowValues = axisValues(rows);
  const colValues = axisValues(cols);
  const near = (a: number, b: number) => Math.abs(a - b) < 1e-9;
  const baseRow = base[rows.field] as number;
  const baseCol = base[cols.field] as number;
  const cells = rowValues.map((rv, ri) =>
    colValues.map((cv, ci) => {
      const s = withField(withField(base, rows.field, rv), cols.field, cv);
      const r = computeOffer(s, costs);
      return {
        row: ri,
        col: ci,
        value: metricOf(r, metric, v),
        marginPct: v === 'byok' ? r.byok.marginPct : r.managed.marginPct,
        isBase:
          near(rv, baseRow) &&
          near(cv, baseCol) &&
          (rows.field !== 'platformPerLearner' && cols.field !== 'platformPerLearner'
            ? true
            : base.platformMode === 'fixed'),
      };
    }),
  );
  return { rowValues, colValues, cells };
}

// ── Kurve über die Nutzung ─────────────────────────────────────────────

export interface UsagePoint {
  usage: number;
  managedRevenue: number;
  managedCost: number;
  byokRevenue: number;
  byokCost: number;
}

export function usageCurve(s: Scenario, c: CostAssumptions, max = 15, step = 0.5): UsagePoint[] {
  const points: UsagePoint[] = [];
  for (let u = 0; u <= max + 1e-9; u += step) {
    const r = computeOffer({ ...s, usagePerLearner: u, usageCase: 'mid' }, c);
    points.push({
      usage: Math.round(u * 100) / 100,
      managedRevenue: r.managed.monthly,
      managedCost: r.managed.costMonthly,
      byokRevenue: r.byok.monthly,
      byokCost: r.byok.costMonthly,
    });
  }
  return points;
}

// ── Varianten ──────────────────────────────────────────────────────────

/**
 * „8000; 14000 20.000" → [8000, 14000, 20000]. Getrennt wird an Semikolon,
 * Leerraum und „, "; ein Token mit einem Komma ist ein Dezimalwert
 * („0,35"), mit mehreren oder langen Zahlen eine Liste („1,3,5",
 * „8000,14000"). „20.000" = Tausenderpunkt.
 */
export function parseValues(text: string): number[] {
  const toNumber = (t: string) =>
    Number(/^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t.replace(',', '.'));
  return text
    .split(/;|\s+|,\s+/)
    .flatMap((t) => (/,.*,|\d{4},|,\d{4}/.test(t) ? t.split(',') : [t]))
    .map((t) => t.trim())
    .filter(Boolean)
    .map(toNumber)
    .filter((n) => Number.isFinite(n));
}
